import { useField } from 'formik';
import { memo, useCallback, useEffect, useRef } from 'react';

const OTP_LENGTH = 6;

const OtpInputField = memo(({ name = 'otp', label = 'Enter the code here' }) => {
  const [field, meta, helpers] = useField(name);
  const inputRefs = useRef([]);
  const hasError = meta.touched && meta.error;

  const digits = Array.from({ length: OTP_LENGTH }, (_, index) => field.value[index] || '');

  const focusInput = useCallback((index) => {
    inputRefs.current[index]?.focus();
  }, []);

  const updateValue = useCallback(
    (nextValue) => {
      const sanitized = nextValue.replace(/\D/g, '').slice(0, OTP_LENGTH);
      helpers.setValue(sanitized);
      return sanitized;
    },
    [helpers],
  );

  const handleChange = useCallback(
    (index, value) => {
      const digit = value.replace(/\D/g, '').slice(-1);
      const nextDigits = [...digits];
      nextDigits[index] = digit;
      const sanitized = updateValue(nextDigits.join(''));
      if (digit && index < OTP_LENGTH - 1) {
        focusInput(index + 1);
      }
      if (!digit && sanitized.length === index) {
        focusInput(Math.max(index - 1, 0));
      }
    },
    [digits, focusInput, updateValue],
  );

  const handleKeyDown = useCallback(
    (index, event) => {
      if (event.key === 'Backspace' && !digits[index] && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }
      if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        event.preventDefault();
        focusInput(index + 1);
      }
    },
    [digits, focusInput],
  );

  const handlePaste = useCallback(
    (event) => {
      event.preventDefault();
      const pasted = event.clipboardData.getData('text');
      const sanitized = updateValue(pasted);
      focusInput(Math.min(sanitized.length, OTP_LENGTH - 1));
    },
    [focusInput, updateValue],
  );

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  return (
    <div className="flex w-full flex-col gap-0.75 desktop:gap-1">
      <label className="desktop:text-[16px] text-sm font-semibold leading-3.75 desktop:leading-5 text-merchant-text-secondary">
        {label}
      </label>
      <div className="flex w-full max-w-otp-group gap-0.75 desktop:gap-1">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={digit}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={handlePaste}
            onBlur={() => {
              if (field.value) {
                helpers.setTouched(true);
              }
            }}
            aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
            className={[
              'size-8 shrink-0 rounded-field border bg-merchant-bg-subtle text-center desktop:text-[16px] text-sm font-medium text-merchant-text-primary outline-none md:size-9 desktop:size-12',
              hasError
                ? 'border-error'
                : 'border-merchant-border focus:border-merchant-action focus:ring-1 focus:ring-merchant-action',
            ].join(' ')}
          />
        ))}
      </div>
      {hasError && <p className="desktop:text-[12px] text-[10px] text-error">{meta.error}</p>}
    </div>
  );
});

OtpInputField.displayName = 'OtpInputField';

export default OtpInputField;
